import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, X, Image, Check, AlertCircle, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBatchUpload() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [library, setLibrary] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(true);

  const token = localStorage.getItem("adminToken");

  useEffect(() => { fetchLibrary(); }, []);

  const fetchLibrary = async () => {
    try {
      setLoadingLibrary(true);
      const res = await axios.get(`${API}/media-library`, { headers: { Authorization: `Bearer ${token}` } });
      setLibrary(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingLibrary(false); }
  };

  const deleteFromLibrary = async (itemId) => {
    if (!window.confirm("Delete this image permanently?")) return;
    try {
      await axios.delete(`${API}/media-library/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
      setLibrary(prev => prev.filter(i => i.id !== itemId));
    } catch (e) { console.error(e); }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (file) => file.type.startsWith("image/")
    );

    if (droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(
      (file) => file.type.startsWith("image/")
    );
    if (selectedFiles.length > 0) {
      addFiles(selectedFiles);
    }
  };

  const addFiles = (newFiles) => {
    const filesWithPreview = newFiles.map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: URL.createObjectURL(file),
      status: "pending", // pending, uploading, success, error
    }));
    setFiles((prev) => [...prev, ...filesWithPreview]);
  };

  const removeFile = (id) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  const uploadAllFiles = async () => {
    if (!token) {
      alert("Please login first");
      navigate("/admin/login");
      return;
    }

    setUploading(true);
    const results = [];

    for (const fileObj of files) {
      if (fileObj.status === "success") continue;

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileObj.id ? { ...f, status: "uploading" } : f
        )
      );

      try {
        const formData = new FormData();
        formData.append("file", fileObj.file);

        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress((prev) => ({ ...prev, [fileObj.id]: percent }));
          },
        });

        results.push({
          id: fileObj.id,
          url: response.data.url,
          filename: fileObj.file.name,
        });

        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileObj.id ? { ...f, status: "success" } : f
          )
        );
      } catch (error) {
        console.error("Upload error:", error);
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileObj.id ? { ...f, status: "error" } : f
          )
        );
      }
    }

    setUploadedUrls((prev) => [...prev, ...results]);
    setUploading(false);
    fetchLibrary();
  };

  const copyAllUrls = () => {
    const urls = uploadedUrls.map((u) => u.url).join("\n");
    navigator.clipboard.writeText(urls);
    alert("All URLs copied to clipboard!");
  };

  const clearAll = () => {
    files.forEach((f) => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setFiles([]);
    setUploadedUrls([]);
    setUploadProgress({});
  };

  const pendingCount = files.filter((f) => f.status === "pending").length;
  const successCount = files.filter((f) => f.status === "success").length;

  return (
    <AdminLayout
      title="Batch Photo Upload"
      subtitle="Drag and drop multiple photos at once"
      actions={
        files.length > 0 ? (
          <Button
            onClick={clearAll}
            variant="outline"
            className="text-[#676664]/60 border-[#282828]/20"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        ) : null
      }
    >
      <div className="max-w-5xl">
        {/* Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
            dragActive
              ? "border-[#9A7B4F] bg-[#9A7B4F]/5"
              : "border-[#282828]/20 hover:border-[#282828]/40"
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#F0D9D5]/50 flex items-center justify-center">
            <Upload className="w-8 h-8 text-[#9A7B4F]" />
          </div>
          <h3 className="text-xl text-[#676664] mb-2">
            Drop your photos here
          </h3>
          <p className="text-[#676664]/60 mb-6">
            or click to browse from your device
          </p>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="file-input"
          />
          <Button
            type="button"
            onClick={() => document.getElementById('file-input').click()}
            className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white"
            data-testid="select-photos-btn"
          >
            <Image className="w-4 h-4 mr-2" />
            Select Photos
          </Button>
        </div>

        {/* File Preview Grid */}
        {files.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg text-[#676664]">
                {files.length} photo{files.length !== 1 ? "s" : ""} selected
                {successCount > 0 && (
                  <span className="text-[#B8C4B8] ml-2">
                    ({successCount} uploaded)
                  </span>
                )}
              </h3>
              {pendingCount > 0 && (
                <Button
                  onClick={uploadAllFiles}
                  disabled={uploading}
                  className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white"
                >
                  {uploading ? (
                    <>Uploading...</>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload {pendingCount} Photo{pendingCount !== 1 ? "s" : ""}
                    </>
                  )}
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {files.map((fileObj) => (
                <div
                  key={fileObj.id}
                  className="relative aspect-square rounded-lg overflow-hidden bg-[#6B5B4F]/5 group"
                >
                  <img
                    src={fileObj.preview}
                    alt={fileObj.file.name}
                    className="w-full h-full object-cover"
                  />

                  {/* Status Overlay */}
                  {fileObj.status === "uploading" && (
                    <div className="absolute inset-0 bg-[#6B5B4F]/70 flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                        <span className="text-sm">
                          {uploadProgress[fileObj.id] || 0}%
                        </span>
                      </div>
                    </div>
                  )}

                  {fileObj.status === "success" && (
                    <div className="absolute inset-0 bg-[#B8C4B8]/80 flex items-center justify-center">
                      <Check className="w-10 h-10 text-white" />
                    </div>
                  )}

                  {fileObj.status === "error" && (
                    <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center">
                      <AlertCircle className="w-10 h-10 text-white" />
                    </div>
                  )}

                  {/* Remove Button */}
                  {fileObj.status === "pending" && (
                    <button
                      onClick={() => removeFile(fileObj.id)}
                      className="absolute top-2 right-2 w-8 h-8 bg-[#6B5B4F]/80 hover:bg-[#6B5B4F] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}

                  {/* Filename */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#6B5B4F]/80 to-transparent p-2">
                    <p className="text-white text-xs truncate">
                      {fileObj.file.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Uploaded URLs */}
        {uploadedUrls.length > 0 && (
          <div className="mt-8 p-6 bg-white rounded-xl border border-[#282828]/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg text-[#676664]">
                Uploaded Photo URLs ({uploadedUrls.length})
              </h3>
              <Button
                onClick={copyAllUrls}
                variant="outline"
                className="text-[#9A7B4F] border-[#9A7B4F]"
              >
                Copy All URLs
              </Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {uploadedUrls.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-2 bg-[#FAF8F0] rounded"
                >
                  <Check className="w-4 h-4 text-[#B8C4B8] flex-shrink-0" />
                  <span className="text-sm text-[#676664]/60 truncate flex-1">
                    {item.filename}
                  </span>
                  <code className="text-xs text-[#9A7B4F] bg-[#9A7B4F]/10 px-2 py-1 rounded">
                    {item.url}
                  </code>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-[#676664]/60">
              Use these URLs when adding outfits or social posts.
            </p>
          </div>
        )}

        {/* Quick Actions */}
        {uploadedUrls.length > 0 && (
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <Link
              to="/admin/outfits"
              className="p-6 bg-white rounded-xl border border-[#282828]/10 hover:border-[#9A7B4F]/30 transition-colors"
            >
              <h4 className="text-lg text-[#676664] mb-2">Add to Collection</h4>
              <p className="text-sm text-[#676664]/60">
                Create new outfit listings with your uploaded photos
              </p>
            </Link>
            <Link
              to="/admin/social"
              className="p-6 bg-white rounded-xl border border-[#282828]/10 hover:border-[#9A7B4F]/30 transition-colors"
            >
              <h4 className="text-lg text-[#676664] mb-2">Add to Social Feed</h4>
              <p className="text-sm text-[#676664]/60">
                Add photos to your homepage carousel
              </p>
            </Link>
          </div>
        )}

        {/* Image Library */}
        <div className="mt-10 pt-8 border-t border-[#282828]/10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl text-[#676664]">Your Image Library</h3>
              <p className="text-sm text-[#676664]/50">All your uploaded images — use these when editing listings or the hero section</p>
            </div>
            <span className="text-sm text-[#676664]/40">{(library || []).length} image{(library || []).length !== 1 ? 's' : ''}</span>
          </div>
          {loadingLibrary ? (
            <div className="text-center py-12"><div className="w-8 h-8 border-2 border-[#9A7B4F] border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : (library || []).length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-dashed border-[#282828]/20">
              <Image className="w-10 h-10 text-[#676664]/20 mx-auto mb-3" />
              <p className="text-[#676664]/40 text-sm">No images in your library yet. Upload some above!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {(library || []).map((item) => (
                <div key={item.id} className="relative aspect-square rounded-lg overflow-hidden bg-[#6B5B4F]/5 group border border-[#282828]/10">
                  <img src={item.url} alt={item.original_name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => { navigator.clipboard.writeText(item.url); alert("URL copied!"); }}
                      className="px-2 py-1 bg-white/90 text-[#676664] text-[10px] rounded mr-1"
                    >Copy URL</button>
                    <button
                      onClick={() => deleteFromLibrary(item.id)}
                      className="w-7 h-7 bg-red-500/90 text-white rounded flex items-center justify-center"
                    ><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                    <p className="text-white text-[9px] truncate">{item.original_name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
